import Navbar from '../services/components/Navbar';
import Footer from '../services/components/Footer';
import SEOHead from '../../components/SEOHead';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Aire University",
    "description": "Meet our expert team: Mariam Fouzia (CEO), Jaquline Sovel (Project Consultant), and Zahid Hayat (Revenue Manager). Combined 65+ years of experience in real estate, hospitality, and revenue optimization.",
    "url": `${siteUrl}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Aire University",
      "url": siteUrl,
      "logo": `${siteUrl}/vite.svg`,
      "description": "Expert Airbnb property setup and management services helping homeowners transform properties into profitable short-term rentals",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2043 Pulaski Hwy",
        "addressLocality": "Newark",
        "addressRegion": "DE",
        "postalCode": "19702",
        "addressCountry": "US"
      },
      "telephone": "+1-302-500-3373",
      "email": "Aireuniversity@gmail.com",
      "founder": {
        "@type": "Person",
        "name": "Mariam Fouzia",
        "jobTitle": "CEO & Founder",
        "description": "Extensive real estate experience with multiple Airbnb property ownership. Passionate about helping hosts become successful entrepreneurs."
      },
      "employee": [
        {
          "@type": "Person",
          "name": "Jaquline Sovel",
          "jobTitle": "Project Consultant",
          "description": "30+ years of hospitality expertise specializing in property transformation and design"
        },
        {
          "@type": "Person",
          "name": "Zahid Hayat",
          "jobTitle": "Revenue Manager",
          "description": "20+ years of revenue optimization experience maximizing host profitability"
        }
      ]
    }
  };

  const teamMembers = [
    {
      name: 'Mariam Fouzia',
      title: 'CEO & Founder',
      image: 'https://public.readdy.ai/ai/img_res/efd953081c022c5ad4c9f874a1db21fa.jpg',
      bio: 'With extensive real estate experience and ownership of several successful Airbnb properties, Mariam brings a wealth of knowledge to every client relationship. Her extensive rental portfolio demonstrates her deep understanding of the short-term rental market. Her passion is helping hosts become successful entrepreneurs, transforming property ownership into profitable business ventures.',
      expertise: ['Real Estate Investment', 'Airbnb Property Management', 'Entrepreneurship', 'Portfolio Development']
    },
    {
      name: 'Jaquline Sovel',
      title: 'Project Consultant',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20interior%20design%20consultant%20in%20stylish%20business%20casual%20attire%2C%20friendly%20approachable%20smile%2C%20creative%20workspace%20background%2C%20professional%20corporate%20headshot%20photography%2C%20diverse%20businesswoman%20designer%2C%20simple%20clean%20background&width=600&height=800&seq=team-consultant-002&orientation=portrait',
      bio: 'With over 30 years of hospitality expertise, Jaquline brings unparalleled experience to every project. Her passion is helping hosts transform their homes into revenue-generating organisms through thoughtful design and strategic planning. She understands that great design isn\'t just about aesthetics—it\'s about creating spaces that guests love and hosts profit from.',
      expertise: ['Interior Design', 'Hospitality Management', 'Space Transformation', 'Guest Experience']
    },
    {
      name: 'Zahid Hayat',
      title: 'Revenue Manager',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20business%20analyst%20executive%20in%20sharp%20business%20attire%2C%20confident%20professional%20smile%2C%20modern%20office%20background%2C%20professional%20corporate%20headshot%20photography%2C%20diverse%20businessman%20expert%2C%20simple%20clean%20background&width=600&height=800&seq=team-revenue-003&orientation=portrait',
      bio: 'With over 20 years of experience in revenue optimization, Zahid is a master at maximizing profitability. His passion is helping hosts achieve their highest revenue potential through data-driven pricing strategies, market analysis, and algorithm optimization. He stays ahead of industry trends to ensure every property performs at its peak.',
      expertise: ['Revenue Optimization', 'Pricing Strategy', 'Market Analysis', 'Algorithm Optimization']
    }
  ];

  return (
    <>
      <SEOHead
        title="About Aire University | Expert Airbnb Property Management Team Newark DE"
        description="Meet the Aire University team: Mariam Fouzia (CEO with extensive real estate experience), Jaquline Sovel (30+ years hospitality expertise), and Zahid Hayat (20+ years revenue optimization). Helping hosts become successful entrepreneurs."
        keywords="Aire University team, Airbnb experts Newark, property management professionals, real estate consultants Delaware, hospitality experts"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-coral-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Meet Our Expert Team
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A powerhouse team of real estate, hospitality, and revenue experts dedicated to transforming your property into a thriving Airbnb business.
            </p>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-coral-500 text-white text-sm font-semibold rounded-full mb-6">
              Our Mission
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Empowering Homeowners to Build Wealth Through Hosting
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-6">
              We believe every homeowner should have the opportunity to generate income from their property. With decades of combined experience in real estate, hospitality, and revenue management, our team has helped hundreds of homeowners successfully launch and manage profitable Airbnb properties.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              From navigating complex building codes to optimizing revenue algorithms, we provide the expertise and support needed to transform your property into a thriving short-term rental business.
            </p>
          </div>
        </section>

        {/* Team Members */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Leadership Team
              </h2>
              <p className="text-xl text-gray-600">
                Meet the experts who will guide your success
              </p>
            </div>

            <div className="space-y-24">
              {teamMembers.map((member, index) => (
                <div 
                  key={member.name}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[600px]">
                      <img 
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="inline-block px-4 py-2 bg-coral-500 text-white text-sm font-semibold rounded-full mb-4">
                      {member.title}
                    </div>
                    
                    <h3 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {member.name}
                    </h3>

                    <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                      {member.bio}
                    </p>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Areas of Expertise:</h4>
                      <div className="flex flex-wrap gap-3">
                        {member.expertise.map((skill) => (
                          <span 
                            key={skill}
                            className="px-4 py-2 bg-white border-2 border-coral-500 text-coral-500 rounded-lg font-medium text-sm"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Track Record
              </h2>
              <p className="text-xl text-gray-600">
                Numbers that speak to our commitment and success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-8 bg-coral-50 rounded-2xl">
                <div className="text-5xl font-bold text-coral-500 mb-3">500+</div>
                <p className="text-lg text-gray-900 font-semibold">Properties Transformed</p>
              </div>
              
              <div className="text-center p-8 bg-coral-50 rounded-2xl">
                <div className="text-5xl font-bold text-coral-500 mb-3">$12M+</div>
                <p className="text-lg text-gray-900 font-semibold">Revenue Generated</p>
              </div>
              
              <div className="text-center p-8 bg-coral-50 rounded-2xl">
                <div className="text-5xl font-bold text-coral-500 mb-3">65+</div>
                <p className="text-lg text-gray-900 font-semibold">Years Combined Experience</p>
              </div>
              
              <div className="text-center p-8 bg-coral-50 rounded-2xl">
                <div className="text-5xl font-bold text-coral-500 mb-3">98%</div>
                <p className="text-lg text-gray-900 font-semibold">Client Satisfaction</p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Core Values
              </h2>
              <p className="text-xl text-gray-600">
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-coral-500 rounded-xl mb-6">
                  <i className="ri-lightbulb-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expertise</h3>
                <p className="text-gray-600 leading-relaxed">
                  We bring decades of combined experience in real estate, hospitality, and revenue management to every project, ensuring you benefit from proven strategies and industry best practices.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-coral-500 rounded-xl mb-6">
                  <i className="ri-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Passion</h3>
                <p className="text-gray-600 leading-relaxed">
                  We're genuinely passionate about helping homeowners succeed. Your success is our success, and we're committed to going above and beyond to help you achieve your goals.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-coral-500 rounded-xl mb-6">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Integrity</h3>
                <p className="text-gray-600 leading-relaxed">
                  We operate with complete transparency and honesty. You'll always know exactly what we're doing, why we're doing it, and what results to expect from our partnership.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-coral-500 to-coral-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Work With Us?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Schedule a free consultation with our team and discover how we can help you maximize your property's potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-white text-coral-500 font-semibold py-4 px-10 rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule Consultation
              </a>
              <a
                href="tel:302-500-3373"
                className="inline-block bg-transparent border-2 border-white text-white font-semibold py-4 px-10 rounded-lg hover:bg-white hover:text-coral-500 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Call: 302-500-3373
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
